import { Form, PageHeader } from 'antd'
import { useEffect, useState } from 'react'

import FormBlock from '../../components/forms/FormBlock'
import FormWrap from '../../components/forms/FormWrap'
import {
  FixSkuMediaForm,
  FixSkuSizesForm,
} from '../../forms/FixSkus'
import { createFixSku } from '../../network/FixSku'
import { useParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { getCurrency } from '../../network/Stores'
import { getCurrencySymbol } from '../../utils/currencyUtils'
import { createUrlWithFiltersFromCurrentURL } from '../../utils/filterUtils'


const CreateFixSku = ({ history }) => {
  const [form] = Form.useForm()
  const { storeId } = useParams()
  const { user } = useAuth()

  const [currencySymbol, setCurrencySymbol] = useState('¥')

  useEffect(() => {
    const fetchCurrency = async () => {
      const currencyValue = await getCurrency(storeId)
      const symbol = getCurrencySymbol(currencyValue.currency)
      setCurrencySymbol(symbol)
    }

    fetchCurrency()
  }, [storeId])

  const FixSkuDescriptionForm = {
    title: 'Description',
    key: [],
    sections: [
      {
        columns: 2,
        fields: [
          {
            label: 'Name',
            placeholder: 'Name',
            isRequired: true,
            key: ['name', 'en'],
            pos: {
              col: 1,
              row: 1,
              size: 1,
            },
          },
          // Display only if the store is not a franchise
          ...(!user.franchiseStore
            ? [
                {
                  label: 'Name (中文)',
                  placeholder: 'Chinese name',
                  isRequired: true,
                  key: ['name', 'zh'],
                  pos: {
                    col: 1,
                    row: 2,
                    size: 1,
                  },
                },
              ]
            : []),
          {
            label: 'SKU',
            placeholder: 'Ref ID',
            isRequired: true,
            key: ['sku'],
            pos: {
              col: 1,
              row: 4,
              size: 1,
            },
          },
          {
            label: `Retail price (${currencySymbol})`,
            isRequired: true,
            key: ['retailPrice'],
            pos: {
              col: 1,
              row: 7,
              size: 1,
            },
          },
          {
            label: 'Delivery time (days)',
            isRequired: true,
            key: ['deliveryTime'],
            pos: {
              col: 1,
              row: 8,
              size: 1,
            },
          },
          {
            label: 'Description',
            type: 'text',
            isRequired: true,
            key: ['description', 'en'],
            pos: {
              col: 1,
              row: 9,
              size: 1,
            },
          },
          // Display only if the store is not a franchise
          ...(!user.franchiseStore
            ? [
                {
                  label: 'Description (中文)',
                  type: 'text',
                  isRequired: true,
                  key: ['description', 'zh'],
                  pos: {
                    col: 1,
                    row: 10,
                    size: 1,
                  },
                },
                {
                  label: 'Gender',
                  type: 'basicRadio',
                  isRequired: false,
                  key: ['gender'],
                  options: [
                    { label: 'Male', key: 'male' },
                    { label: 'Female', key: 'female' },
                    { label: 'Both', key: 'both' },
                  ],
                  pos: {
                    col: 1,
                    row: 11,
                    size: 1,
                  },
                },
                {
                    label: 'Customizable',
                    type: 'basicRadio',
                    isRequired: true,
                    key: ['customizable'],
                    options: [
                        { label: 'Yes', key: 'true' },
                        { label: 'No', key: 'false' },
                    ],
                    pos: {
                        col: 1,
                        row: 12,
                        size: 1,
                    },
                },
                {
                    label: 'Comment',
                    type: 'text',
                    isRequired: false,
                    key: ['comment'],
                    pos: {
                        col: 1,
                        row: 13,
                        size: 1,
                    },
                }
              ]
            : []),
        ],
      },
    ],
  }

  return (
    <>
      <PageHeader
        className="site-page-header"
        title={'Create fix SKU'}
        onBack={() => {
          const url = createUrlWithFiltersFromCurrentURL('/fixed-sku')
          history.push(url)
        }}
        style={{ backgroundColor: '#fff' }}
      />
      <FormWrap
        form={form}
        title="create_fix_sku"
        request={createFixSku}
        cancelText={'Cancel'}
        okText={'Save'}
        onSuccess={() => {
          const url = createUrlWithFiltersFromCurrentURL('/fixed-sku')
          history.push(url)
        }}
        cleanBeforeSending={(oldData) => {
          oldData.store = storeId
          
          // Clean up null/undefined values from details
          if (oldData.details && Array.isArray(oldData.details)) {
            oldData.details = oldData.details.map(detail => {
              const cleanedDetail = {}
              
              if (detail._id) {
                cleanedDetail._id = detail._id
              }
              
              // For each product type (shoes, vest, pant, etc.)
              Object.keys(detail).forEach(productType => {
                if (productType === '_id') return
                
                if (detail[productType] && typeof detail[productType] === 'object') {
                  const cleanedProductData = {}
                  
                  // Remove null/undefined values from product data
                  Object.keys(detail[productType]).forEach(key => {
                    const value = detail[productType][key]
                    if (value !== null && value !== undefined && value !== '') {
                      cleanedProductData[key] = value
                    }
                  })
                }
              })
              
              return cleanedDetail
            })
          }
          
          if (oldData.mainPicture) {
            let main = oldData.mainPicture.fileList
              ? oldData.mainPicture?.fileList
              : oldData.mainPicture

            if (!Array.isArray(main)) {
              main = [main]
            }
            const newPic = main.map((file) =>
              file.response ? file.response : file
            )

            oldData.mainPicture = newPic.length ? newPic[0] : {}
          } else {
            oldData.mainPicture = {}
          }

          if (oldData.otherPictures) {
            const otherPic = oldData.otherPictures.fileList
              ? oldData.otherPictures?.fileList
              : oldData.otherPictures
            oldData.otherPictures = otherPic.map((file) =>
              file.response ? file.response : file
            )
          } else {
            oldData.otherPictures = []
          }

          return oldData
        }}
        // For debugging form data change
        onValuesChange={(changedValues, allValues) => {
          // console.log(allValues)
        }}
        invalidate={['getFixSku']}
      >
        <div className="section-from-vertical">
          <FormBlock form={form} formTemplate={FixSkuDescriptionForm} />
          <FormBlock form={form} formTemplate={FixSkuSizesForm} />
          {!user.franchiseStore && (
            <FormBlock form={form} formTemplate={FixSkuMediaForm} />
          )}

        </div>
      </FormWrap>
    </>
  )
}

export default CreateFixSku
